import React, { useEffect, useMemo, useState } from "react";
import useStyles from "./style";
import { Button, Grid, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import _ from "lodash";

import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

import UploadFiles from "./UploadFiles";
import ViewFiles from "./ViewFiles";
import {
  buildEmptyPassengerDocuments,
  isBlobUrl,
  resolveMediaUrl,
} from "./documents";
import PassengerApi from "Services/Passenger";

const PassengerDocuments = ({
  passengerId,
  documents,
  setDocuments,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [uploadFilesModal, setUploadFilesModal] = useState(false);
  const [viewFilesModal, setViewFilesModal] = useState(false);
  const [documentData, setDocumentData] = useState(null);

  useEffect(() => {
    if (!Array.isArray(documents) || documents.length === 0) {
      setDocuments(buildEmptyPassengerDocuments());
    }
  }, [documents, setDocuments]);

  const handleOpenUploadFilesModal = (row) => {
    setDocumentData(row);
    setUploadFilesModal(true);
  };

  const handleCloseUploadFilesModal = () => {
    setDocumentData(null);
    setUploadFilesModal(false);
  };

  const handleOpenViewFilesModal = (row) => {
    setDocumentData(row);
    setViewFilesModal(true);
  };

  const handleCloseViewFilesModal = () => {
    setDocumentData(null);
    setViewFilesModal(false);
  };

  const updateDocumentRow = (documentId, updates) => {
    setDocuments((previousDocuments) =>
      previousDocuments.map((item) =>
        item.documentId === documentId ? { ...item, ...updates } : item
      )
    );
  };

  const handleUploadDocument = (file) => {
    if (!documentData || !file || !passengerId) {
      return Promise.reject(new Error("Missing passenger document context"));
    }

    dispatch(showLoader("Loading please wait..."));

    const formData = new FormData();
    formData.append("image", file);
    formData.append("user_id", passengerId);
    formData.append("document_type_id", documentData.documentId);

    const onSuccess = (res) => {
      dispatch(hideLoader());

      const previousFile = documentData?.file;
      if (isBlobUrl(previousFile)) {
        URL.revokeObjectURL(previousFile);
      }

      const uploadedDocumentPath = res?.data?.data?.documentPath || "";
      const previewUrl = resolveMediaUrl(uploadedDocumentPath) || URL.createObjectURL(file);

      updateDocumentRow(documentData.documentId, {
        file: previewUrl,
        fileName: file.name,
        mimeType: file.type || "",
        status: res?.data?.data?.status || "ACCESSING",
        rawFile: null,
      });

      dispatch(
        showSnackbar({
          message: res?.data?.message || "Document uploaded successfully",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        })
      );
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to upload document",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      return Promise.reject(err);
    };

    return PassengerApi.updatePassengerDocument(formData).then(onSuccess).catch(onFailure);
  };

  const handleDeleteDocument = (row) => {
    if (!row) {
      return Promise.reject(new Error("Missing document row"));
    }

    const result = window.confirm("Do you want to Delete?");
    if (!result) {
      return Promise.resolve();
    }

    if (!passengerId) {
      return Promise.reject(new Error("Missing passenger id"));
    }

    dispatch(showLoader("Loading please wait..."));

    const data = {
      document_type_id: row?.documentId,
      user_id: passengerId,
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());

      if (isBlobUrl(row?.file)) {
        URL.revokeObjectURL(row.file);
      }

      updateDocumentRow(row.documentId, {
        file: "",
        fileName: "",
        mimeType: "",
        status: "NO DOCUMENT",
        rawFile: null,
      });

      dispatch(
        showSnackbar({
          message: res?.data?.message || "Document deleted successfully",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        })
      );

      handleCloseViewFilesModal();
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to delete the document",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      return Promise.reject(err);
    };

    return PassengerApi.deletePassengerDocument({ ...data }).then(onSuccess).catch(onFailure);
  };

  const handleApproveDocument = (row) => {
    if (!row) {
      return Promise.reject(new Error("Missing document row"));
    }

    if (!passengerId) {
      return Promise.reject(new Error("Missing passenger id"));
    }

    dispatch(showLoader("Loading please wait..."));

    const data = {
      document_type_id: row?.documentId,
      is_initial_document: true,
      status: "APPROVED",
      user_id: passengerId,
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());

      updateDocumentRow(row.documentId, {
        status: "APPROVED",
      });

      dispatch(
        showSnackbar({
          message: res?.data?.message || "Document status updated successfully",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        })
      );

      handleCloseViewFilesModal();
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to update document status",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      return Promise.reject(err);
    };

    return PassengerApi.updatePassengerDocument({ ...data }).then(onSuccess).catch(onFailure);
  };

  const dataRows = useMemo(
    () =>
      _.map(documents || [], (data, index) => ({
        id: data?.documentId || index + 1,
        sno: index + 1,
        documentId: data?.documentId,
        documentType: data?.documentType,
        documentTypeAlias: data?.documentTypeAlias,
        file: data?.file,
        fileName: data?.fileName,
        mimeType: data?.mimeType,
        status: data?.status,
        rawFile: data?.rawFile,
      })),
    [documents]
  );

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      minWidth: 180,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className={classes.lightText}>
          <Typography variant="body2">{params?.row?.sno}</Typography>
        </div>
      ),
    },
    {
      field: "documentType",
      headerName: "Document Type",
      minWidth: 200,
      sortable: true,
      renderCell: (params) => (
        <div>
          <Typography variant="body2">{params?.row?.documentType}</Typography>
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 200,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div>
          <Typography variant="body2">{params?.row?.status}</Typography>
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 320,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const hasFile = Boolean(params?.row?.file);

        return (
          <div className={classes.actionBtnWrapper}>
            {!hasFile && (
              <section className={classes.uploadButton}>
                <Button
                  variant="outlined"
                  style={{ fontSize: "13px", borderRadius: "8px" }}
                  onClick={() => handleOpenUploadFilesModal(params?.row)}
                >
                  Upload
                </Button>
              </section>
            )}

            {hasFile && (
              <>
                <section className={classes.viewButton}>
                  <Button
                    variant="outlined"
                    style={{ fontSize: "13px", borderRadius: "8px" }}
                    onClick={() => handleOpenViewFilesModal(params?.row)}
                  >
                    View
                  </Button>
                </section>

                <section className={classes.deleteButton}>
                  <Button
                    variant="outlined"
                    style={{ fontSize: "13px", borderRadius: "8px" }}
                    onClick={() => handleDeleteDocument(params?.row)}
                  >
                    Delete
                  </Button>
                </section>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Grid container className={classes.root}>
      <Grid item xs={12}>
        <section className={classes.dataGridTable}>
          <DataGrid
            rows={dataRows}
            columns={columns}
            autoHeight={true}
            disableSelectionOnClick
            scrollbarSize={4}
            rowHeight={70}
            style={{ padding: "10px", borderRadius: "0px" }}
          />
        </section>
      </Grid>
      <UploadFiles
        openDialog={uploadFilesModal}
        handleClose={handleCloseUploadFilesModal}
        documentData={documentData}
        onUploadDocument={handleUploadDocument}
      />
      <ViewFiles
        openDialog={viewFilesModal}
        handleClose={handleCloseViewFilesModal}
        documentData={documentData}
        onDeleteDocument={handleDeleteDocument}
        onApproveDocument={handleApproveDocument}
      />
    </Grid>
  );
};

export default PassengerDocuments;
